import type { LogoutType } from '@luna/utils-ts';
import { createSingleton } from '@luna/utils';
import { flushTrackingQueue, stopTracking, trackEvent2 } from '@shared/statistics';
import { logger } from '@luna/logger';
import { triggerGlobalEvent } from '@luna/events';
import { deleteAllCookies } from '@luna/cookies';

export type LogoutRequestInfo = {
    url?: string;
};
export type LogoutConfig = {
    logoutCallback: (logoutType?: LogoutType, requestInfo?: LogoutRequestInfo) => void;
    clearToken: () => void;
};

const { set: setLogout, get: getLogout } = createSingleton<LogoutConfig>();

export function initLogout(newLogout: LogoutConfig): void {
    setLogout(newLogout);
}

const FLUSH_QUEUE_TIMEOUT = 1000;

const logoutInProcess = createSingleton<boolean>(false);

export function resetLogoutInProcessInternally() {
    logoutInProcess.set(false);
}

export async function logout(
    logoutType?: LogoutType,
    requestInfo?: LogoutRequestInfo,
): Promise<void> {
    const logoutImplementation = getLogout()?.logoutCallback;
    if (!logoutImplementation) {
        throw new Error('Logout Implementation not found! Please provide one for initLogout');
    }

    if (logoutInProcess.get()) {
        return;
    }

    logoutInProcess.set(true);
    triggerGlobalEvent('shell:logout', logoutType ?? 'unknown');

    try {
        trackEvent2('sessions', 'logout', {
            reason: logoutType ?? 'unknown',
        });
        trackEvent2('sessionLux', 'userLoggedOut', {
            eventType: 'close_application',
            eventKey: 'session_002',
        });
        await Promise.race([
            flushTrackingQueue(),
            new Promise((res) => {
                setTimeout(() => {
                    logger.debug('Flushing queue time-outed.');
                    res(null);
                }, FLUSH_QUEUE_TIMEOUT);
            }),
        ]);
    } catch (e) {
        logger.error('Flushing queue before logout failed', e);
    }

    stopTracking();

    logoutImplementation(logoutType, requestInfo);
    // we can clear only cookies here, as we need sessionStorage to invalid token later on
    deleteAllCookies();
}

/**
 * Removes the authentication token.
 * Used in handover mechanism, where user is redirected to 3rd party app.
 */
export function removeAuthToken(): void {
    getLogout()?.clearToken?.();
}
